import {useTranslation} from 'next-i18next';
import PropTypes from 'prop-types';
import {pathOr} from 'ramda';
import {useCallback, useContext, useEffect, useState} from 'react';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import useUserActions from '@/src/app/store/user/useUserActions';
import FeedbackVote from '@/src/howTo/components/feedback/FeedbackVote';
import TotalLikes from '@/src/howTo/components/feedback/TotalLikes';
import ResultContactUsFormLayout from '@/src/howTo/components/ResultContactUsFormLayout';
import SuccessMessagePopup from '@/src/howTo/components/SuccessMessagePopup';
import getArticleLikes from '@/src/howTo/utils/getArticleLikes';
import sendFeedback from '@/src/howTo/utils/sendFeedback';
import PopupContext from '@/src/popup/utils/PopupContext';

const LOCAL_STORAGE_VOTED_IDS = 'wiki-how-voted-ids';

const Feedback = ({pageId, children}) => {
  const [isVoted, setIsVoted] = useState(false);
  const [isVotedLoading, setIsVotedLoading] = useState(true);
  const [likesCount, setLikesCount] = useState(0);

  const {openPopup, closePopup} = useContext(PopupContext);
  const {t} = useTranslation('common');

  const {user} = useUserActions();
  const email = pathOr('', ['user', 'email'], user);

  const asPath =
    typeof window !== 'undefined'
      ? pathOr('', ['location', 'href'], window)
      : '';

  const handleLikeClick = useCallback(async () => {
    await sendFeedback(true, pageId, asPath, undefined);

    setVotedToLocalStorage(pageId);
    setIsVoted(true);
    setLikesCount(likesCount + 1);
  }, [pageId, asPath, likesCount]);

  const handleDislikeClick = useCallback(() => {
    // @ts-ignore
    openPopup({
      popup: (
        <ResultContactUsFormLayout
          isNotRequired
          email={email}
          isHideEmail={email.length}
          onCancel={handleCancelSubmit}
          onSubmit={handleSubmitForm}
        />
      ),
      options: {withCloseButton: true},
    });
  }, [pageId, asPath]);

  const handleSubmitForm = useCallback(
    async (values) => {
      const {status} = await sendFeedback(false, pageId, asPath, values);

      setIsVoted(true);
      closePopup();

      if (status === RESPONSE_STATUSES.SUCCESS) {
        setVotedToLocalStorage(pageId);
        closePopup();

        // @ts-ignore
        openPopup({
          popup: (
            <SuccessMessagePopup
              title={t('wiki-how.thank-you-for-message')}
              description={t('wiki-how.feedback-was-send')}
              onClose={closePopup}
            />
          ),
          options: {withCloseButton: true},
        });
      }
    },
    [pageId, asPath]
  );

  const handleCancelSubmit = useCallback(async () => {
    await sendFeedback(false, pageId, asPath, undefined);
    closePopup();
  }, [pageId, asPath]);

  const getPageInfo = useCallback(async () => {
    setIsVotedLoading(true);

    const {status, responseBody} = await getArticleLikes(pageId);

    if (status === RESPONSE_STATUSES.SUCCESS) {
      setIsVoted(
        pathOr(false, ['article', 'is_liked'], responseBody) ||
          getIsVotedFromLocalStorage(pageId)
      );
      setLikesCount(pathOr(0, ['article', 'likes'], responseBody));
    } else {
      setLikesCount(0);
    }
    setIsVotedLoading(false);
  }, [pageId, asPath]);

  const setVotedToLocalStorage = useCallback((id) => {
    const ids = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_VOTED_IDS) || '[]'
    );
    localStorage.setItem(LOCAL_STORAGE_VOTED_IDS, JSON.stringify([...ids, id]));
  }, []);

  const getIsVotedFromLocalStorage = useCallback((id) => {
    const ids = JSON.parse(
      localStorage.getItem(LOCAL_STORAGE_VOTED_IDS) || '[]'
    );
    localStorage.getItem(LOCAL_STORAGE_VOTED_IDS);

    return ids.includes(id);
  }, []);

  useEffect(() => {
    getPageInfo();
  }, [asPath]);

  return (
    <div>
      <TotalLikes
        likesCount={likesCount}
        isLoading={isVotedLoading}
        isVoted={isVoted}
      />

      {children}

      <FeedbackVote
        isVoted={isVoted}
        onLikeClick={handleLikeClick}
        onDislikeClick={handleDislikeClick}
      />
    </div>
  );
};

Feedback.propTypes = {
  pageId: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default Feedback;
